import { useTheme } from "@mui/material/styles";
import { lazy, memo, Suspense, useMemo } from "react";
import { CircularProgressbar, buildStyles } from "react-circular-progressbar";
import { Box, Card, Skeleton, Typography, styled } from "@mui/material";
import successIcon from "../../Images/successicon.png";
const Chart = lazy(() => import("react-apexcharts"));

const BoxWrapper = styled(Box)(({ theme }) => ({
  height: "100%",
  "& .healthWrapper": { filter: "blur(2px)", pointerEvents: "none" },
  "& .GraphWrapper": {
    width: 158,
    height: 158,
    marginLeft: "auto",
    marginRight: "auto",
    position: "relative",
    marginTop: "33px",
    "& span.Status": {
      position: "absolute",
      bottom: "14px",
      left: "0",
      right: "0",
      textAlign: "center",
    },
    "& .innerChartContent": {
      position: "absolute",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      textAlign: "center",
      "& h4": {
        fontSize: "50px",
        color: theme.palette.text.primary,
        fontWeight: 700,
        lineHeight: "63px",
        textAlign: "center",
        textUnderlinePosition: "from-font",
        textDecorationSkipInk: "none",
      },
      "& p": {
        color: theme.palette.text.primary,
        fontSize: "12px",
        fontWeight: 600,
        lineHeight: "15.12px",
        textAlign: "left",
        textUnderlinePosition: "from-font",
        textDecorationSkipInk: "none",
      },
    },
  },
  "& .Subtitle": { textAlign: "center" },
  "& .CircularProgressbar-path": { stroke: "#00CF00" },
  "& .CircularProgressbar-text": { fill: theme.palette.text.primary, fontSize: "22px" },
  "& .bottomContent": {
    textAlign: "center",
    "& h3": {
      color: theme.palette.text.primary,
      textAlign: "center",
      marginBottom: "10px",
      fontSize: "16px",
      fontWeight: 600,
      lineHeight: "20.16px",
      textUnderlinePosition: "from-font",
      textDecorationSkipInk: "none",
    },
  },
  "& .Updated": {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "6px",
    border: "1px solid #00CF00",
    padding: "4px 10px 4px 4px",
    borderRadius: "50px",
    "& img": {
      width: "16px",
      height: "16px",
    },
    "& span": {
      color: "#00CF00",
      fontSize: "12px",
      fontWeight: 600,
      lineHeight: "1",
    },
  },
  "& .dificultyHead": {
    display: "flex",
    justifyContent: "space-between",
    "& h2": {
      color: theme.palette.text.primary,
      fontSize: "18px",
      fontWeight: 700,
      lineHeight: "22.68px",
      textAlign: "left",
      textUnderlinePosition: "from-font",
      textDecorationSkipInk: "none",
    },
  },
}));

function DifficultyChart({ difficultyScore, loading = true }) {
  // const [loading, setLoading] = useState(true);
  const theme = useTheme();
  const primary = theme.palette.primary.main;

  const getDifficultyLabel = score => {
    if (score > 0 && score <= 33) return "Very Easy";
    if (score >= 33 && score <= 50) return "Easy";
    if (score >= 50 && score <= 75) return "Moderate";
    return "Difficult";
  };

  const difficultyLabel = getDifficultyLabel(difficultyScore);

  const optionsColumnChart = useMemo(() => {
    const categoriesToStore = ["Low", "", "", "", "", "", "", "", "", "High"];

    return {
      chart: {
        type: "bar",
        fontFamily: "'Plus Jakarta Sans', sans-serif;",
        foreColor: "#adb0bb",
        toolbar: {
          show: false,
        },
        height: 500,
      },
      colors: [primary],
      plotOptions: {
        bar: {
          borderRadius: 4,
          columnWidth: "80%",
          distributed: true,
          endingShape: "rounded",
        },
      },
      dataLabels: {
        enabled: false,
      },
      legend: {
        show: false,
      },
      grid: {
        yaxis: {
          lines: {
            show: false,
          },
        },
      },
      xaxis: {
        categories: categoriesToStore,
        axisBorder: {
          show: false,
        },
      },
      yaxis: {
        labels: {
          show: false,
        },
      },
      tooltip: {
        theme: theme.palette.mode === "dark" ? "dark" : "light",
      },
    };
  }, [theme, primary]);

  const seriesColumnChart = useMemo(() => {
    const dataToStore = [10, 10, 10, 10, 10, 10, 10, 10, 19, 10];

    return [{ name: "", data: dataToStore }];
  }, []);

  return (
    <BoxWrapper>
      <Card sx={{ padding: "24px 24px 24px 24px", border: `1px solid ${theme.palette.divider}`, boxShadow: "none !important", height: "100%" }}>
        <Box className="dificultyHead">
          <Typography component="h2">Difficulty</Typography>
          <Box className="Updated">
            <Typography component="img" src={successIcon} alt="" />
            <Typography component="span">updated</Typography>
          </Box>
        </Box>
        {loading ? (
          <Box sx={{ width: "100%", margin: "12 auto", padding: "20px" }}>
            <Skeleton variant="rectangular" width="100%" height={300} />
          </Box>
        ) : (
          <>
            <Box className={`GraphWrapper ${difficultyScore ? "" : "healthWrapper"}`}>
              <Box sx={{ position: "relative" }}>
                <CircularProgressbar
                  value={difficultyScore || 0}
                  circleRatio={0.75}
                  strokeWidth={10}
                  styles={buildStyles({
                    rotation: 1 / 2 + 1 / 8,
                    strokeLinecap: "butt",
                    trailColor: "#eee",
                    path: {
                      stroke: "#000",
                    },
                    text: {
                      fill: "#f88 !important",
                      fontSize: "5px",
                    },
                  })}
                />
                <Box className="innerChartContent">
                  <Typography component="h4">{difficultyScore}</Typography>
                  <Typography component="p">out of 100</Typography>
                </Box>
              </Box>
            </Box>
            <Box className="bottomContent">
              <Typography component="h3">{difficultyLabel}</Typography>
              <Typography variant="subtitle2" color="textSecondary" className="Subtitle">
                You have a very high chance of ranking for this
                <br /> keyword without any effort.{" "}
              </Typography>
            </Box>
          </>
        )}

        <Box className="HealthLineChart" sx={{ display: "none" }}>
          <Suspense fallback={<div>Loading...</div>}>
            <Chart
              className={` ${difficultyScore ? "healthWrappernoblr" : "healthWrapper"}`}
              options={optionsColumnChart}
              series={seriesColumnChart}
              type="bar"
              width="100%"
              height="200px"
            />
          </Suspense>
        </Box>
      </Card>
    </BoxWrapper>
  );
}

export default memo(DifficultyChart);
